import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Plus, Trash2 } from "lucide-react";
import {
  Field,
  TextInput,
  TextArea,
  SelectInput,
  FormActions,
  CollapsibleSection,
  ReorderButtons,
  CTAGroup,
  SectionDivider,
} from "../../components/FormField";
import { MediaImageField } from "../../components/MediaPickerDialog";
import { showSuccess, showError, showSeoWarnings } from "../../lib/toast";
import { moveItem } from "../templates/shared";
import { generateUUID } from "../../commonjquery/CommonJquery";
import {
  server_post_data,
  industry_page_save_update,
  get_industry_page_data,
} from "../../serviceconnection/ServiceConnection";
import { SEOSection } from "../../components/SEOSection";
import { ICON_OPTIONS } from "../../lib/iconsData";

const CATEGORIES = [
  { value: "Industries We Serve", label: "Industries We Serve" },
];

// ─── Reusable plain string repeater ──────────────────────────────────────────
const StringRepeater = ({ items, setItems, placeholder, addBtn, addStyle }) => (
  <div className="space-y-2">
    {items.map((val, i) => (
      <div key={i} className="flex gap-2">
        <TextInput
          value={val}
          onChange={(e) =>
            setItems(items.map((x, idx) => (idx === i ? e.target.value : x)))
          }
          placeholder={`${placeholder} ${i + 1}`}
          className="flex-1"
        />
        <ReorderButtons
          index={i}
          total={items.length}
          onMove={(f, t) => setItems(moveItem(items, f, t))}
        />
        {items.length > 1 && (
          <button
            type="button"
            onClick={() => setItems(items.filter((_, idx) => idx !== i))}
            className="text-destructive"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
    ))}
    <button
      type="button"
      onClick={() => setItems([...items, ""])}
      className={addBtn}
      style={addStyle}
    >
      <Plus className="w-3.5 h-3.5" /> Add
    </button>
  </div>
);

export function IndustryForm({ mode }) {
  const navigate = useNavigate();
  const { id } = useParams();

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [uuid, setUuid] = useState("");

  // ─── Basic ────────────────────────────────────────────────────────────────
  const [title, setTitle] = useState("");
  const [category, setCategory] = useState("Industries We Serve");

  // ─── SEO ──────────────────────────────────────────────────────────────────
  const [seoData, setSeoData] = useState({});
  const handleSeoChange = (field, value) =>
    setSeoData((prev) => ({ ...prev, [field]: value }));

  // ─── Section toggles ──────────────────────────────────────────────────────
  const [sections, setSections] = useState({
    hero: true,
    snapshot: true,
    challenges: true,
    weHelp: true,
    techStack: true,
    finalCta: true,
  });
  const toggle = (k) => setSections((s) => ({ ...s, [k]: !s[k] }));

  // ─── HERO ─────────────────────────────────────────────────────────────────
  const [hero, setHero] = useState({
    heading: "",
    highlight: "",
    subheading: "",
    bgImageUrl: "",
    bgImageAlt: "",
  });
  const [bannerPoints, setBannerPoints] = useState([""]);
  const [bannerPrimary, setBannerPrimary] = useState({ text: "", url: "" });
  const [bannerSecondary, setBannerSecondary] = useState({ text: "", url: "" });
  const [bannerBadges, setBannerBadges] = useState([
    { icon: "Shield", title: "", decp: "" },
  ]);

  // ─── SNAPSHOT ─────────────────────────────────────────────────────────────
  const [snapshot, setSnapshot] = useState({
    heading: "",
    description: "",
    imageUrl: "",
    btnText: "",
    btnLink: "",
  });

  // ─── CHALLENGES & SOLUTIONS ───────────────────────────────────────────────
  const [challenges, setChallenges] = useState({
    heading: "",
    description: "",
    btnText: "",
    btnLink: "",
  });
  const [commonChallenges, setCommonChallenges] = useState([""]);
  const [ourSolutions, setOurSolutions] = useState([""]);

  // ─── WE HELP ──────────────────────────────────────────────────────────────
  const [weHelp, setWeHelp] = useState({
    heading: "",
    description: "",
    btnText: "",
    btnLink: "",
  });
  const [weHelpList, setWeHelpList] = useState([
    { icon: "CheckCircle2", title: "", description: "" },
  ]);

  // ─── TECH STACK ───────────────────────────────────────────────────────────
  const [techStack, setTechStack] = useState({
    heading: "",
    description: "",
    btnText: "",
    btnLink: "",
  });
  const [techStackList, setTechStackList] = useState([
    { title: "", desc: "", link: "" },
  ]);
  // const [techStackList, setTechStackList] = useState([{ name: "", image: "" }]);

  // ─── FINAL CTA ────────────────────────────────────────────────────────────
  const [finalCta, setFinalCta] = useState({
    heading: "",
    description: "",
  });
  const [ctaPrimary, setCtaPrimary] = useState({ text: "", url: "" });
  const [ctaSecondary, setCtaSecondary] = useState({ text: "", url: "" });

  // ─── FETCH existing data on edit ──────────────────────────────────────────
  useEffect(() => {
    if (mode === "edit" && id) {
      const fetchExisting = async () => {
        try {
          const formData = new FormData();
          formData.append("main_id", id);
          const response = await server_post_data(
            get_industry_page_data,
            formData,
          );
          const d = response.data?.data ?? null;
          if (!d) return;

          setTitle(d.industry_name ?? "");
          setCategory(d.industry_category ?? "Industries We Serve");
          setUuid(d.uuid ?? generateUUID());

          setSections({
            hero: true,
            snapshot: d.show_capabilities ?? true,
            challenges: true,
            weHelp: true,
            techStack: true,
            finalCta: true,
          });

          setHero({
            heading: d.banner_heading ?? "",
            highlight: d.banner_highlight_word ?? "",
            subheading: d.banner_subheading ?? "",
            bgImageUrl: d.banner_bg_image ?? "",
            bgImageAlt: d.banner_bg_image_alt ?? "",
          });
          setBannerPoints(d.banner_points?.length ? d.banner_points : [""]);
          setBannerPrimary({
            text: d.banner_btn1_text ?? "",
            url: d.banner_btn1_link ?? "",
          });
          setBannerSecondary({
            text: d.banner_btn2_text ?? "",
            url: d.banner_btn2_link ?? "",
          });
          setBannerBadges(
            d.banner_badges?.length
              ? d.banner_badges
              : [{ icon: "Shield", title: "", subtitle: "" }],
          );

          setSnapshot({
            heading: d.snapshot_heading ?? "",
            description: d.snapshot_description ?? "",
            imageUrl: d.snapshot_image ?? "",
            btnText: d.snapshot_btn_text ?? "",
            btnLink: d.snapshot_btn_link ?? "",
          });

          setChallenges({
            heading: d.challange_heading ?? "",
            description: d.challange_description ?? "",
            btnText: d.challange_btn_text ?? "",
            btnLink: d.challange_btn_link ?? "",
          });
          setCommonChallenges(
            d.common_challenges?.length ? d.common_challenges : [""],
          );
          setOurSolutions(d.our_solutions?.length ? d.our_solutions : [""]);

          setWeHelp({
            heading: d.we_help_heading ?? "",
            description: d.we_help_description ?? "",
            btnText: d.we_help_btn_text ?? "",
            btnLink: d.we_help_btn_link ?? "",
          });
          setWeHelpList(
            d.we_help_list?.length
              ? d.we_help_list
              : [{ icon: "CheckCircle2", title: "", description: "" }],
          );

          setTechStack({
            heading: d.tech_stack_heading ?? "",
            description: d.tech_stack_description ?? "",
            btnText: d.tech_stack_btn_text ?? "",
            btnLink: d.tech_stack_btn_link ?? "",
          });
          setTechStackList(
            d.tech_stack_list?.length
              ? d.tech_stack_list.map((t) => ({
                  title: t.title ?? "",
                  desc: t.desc ?? "",
                  link: t.link ?? "",
                }))
              : [{ title: "", desc: "", link: "" }],
          );
          // setTechStackList(
          //   d.tech_stack_list?.length
          //     ? d.tech_stack_list.map((t) =>
          //         typeof t === "string"
          //           ? { name: t, image: "" }
          //           : { name: t.name ?? "", image: t.image ?? "" },
          //       )
          //     : [{ name: "", image: "" }],
          // );

          setFinalCta({
            heading: d.cta_heading ?? "",
            description: d.cta_description ?? "",
          });
          setCtaPrimary({
            text: d.cta_btn1_text ?? "",
            url: d.cta_btn1_link ?? "",
          });
          setCtaSecondary({
            text: d.cta_btn2_text ?? "",
            url: d.cta_btn2_link ?? "",
          });

          const sd = d.seo_data ?? {};
          setSeoData({
            seo_id: sd.seo_id ?? 0,
            title_name: sd.title_name ?? "",
            description: sd.description ?? "",
            image: sd.image ?? "",
            actual_url: sd.actual_url ?? "",
            link_url: sd.link_url ?? "",
            favicon: sd.favicon ?? "",
            keywords: sd.keywords ?? "",
            changefreq: sd.changefreq ?? "",
            priority_screen: sd.priority ?? "0.8",
          });
        } catch (err) {
          console.error("Failed to load industry:", err);
          showError("Failed to load industry data. Please try again.");
        }
      };
      fetchExisting();
    }
  }, [mode, id]);

  // ─── Validation ───────────────────────────────────────────────────────────
  const validate = () => {
    const e = {};
    if (!title.trim()) e.title = "Title is required";
    return e;
  };

  // ─── BUILD PAYLOAD ────────────────────────────────────────────────────────
  const buildPayload = (saveStatus) => {
    const uuId = mode === "edit" ? uuid || generateUUID() : generateUUID();
    return {
      p_id: mode === "edit" ? Number(id) : 0,
      uuid: uuId,
      // p_id:0,
      // uuid: generateUUID(),
      status: saveStatus,
      industry_name: title,
      industry_category: category,

      banner_heading: hero.heading,
      banner_highlight_word: hero.highlight,
      banner_subheading: hero.subheading,
      banner_bg_image: hero.bgImageUrl,
      banner_bg_image_alt: hero.bgImageAlt,
      banner_points: bannerPoints.filter(Boolean),
      banner_badges: bannerBadges.filter((s) => s.title || s.decp),
      banner_btn1_text: bannerPrimary.text,
      banner_btn1_link: bannerPrimary.url,
      banner_btn2_text: bannerSecondary.text,
      banner_btn2_link: bannerSecondary.url,

      show_capabilities: sections.snapshot,
      snapshot_heading: snapshot.heading,
      snapshot_description: snapshot.description,
      snapshot_image: snapshot.imageUrl,
      snapshot_btn_text: snapshot.btnText,
      snapshot_btn_link: snapshot.btnLink,

      challange_heading: challenges.heading,
      challange_description: challenges.description,
      common_challenges: commonChallenges.filter(Boolean),
      our_solutions: ourSolutions.filter(Boolean),
      challange_btn_text: challenges.btnText,
      challange_btn_link: challenges.btnLink,

      we_help_heading: weHelp.heading,
      we_help_description: weHelp.description,
      we_help_list: weHelpList.filter((c) => c.title),
      we_help_btn_text: weHelp.btnText,
      we_help_btn_link: weHelp.btnLink,

      tech_stack_heading: techStack.heading,
      tech_stack_description: techStack.description,
      tech_stack_list: techStackList.filter((t) => t.title),
      tech_stack_btn_text: techStack.btnText,
      tech_stack_btn_link: techStack.btnLink,

      cta_heading: finalCta.heading,
      cta_description: finalCta.description,
      cta_btn1_text: ctaPrimary.text,
      cta_btn1_link: ctaPrimary.url,
      cta_btn2_text: ctaSecondary.text,
      cta_btn2_link: ctaSecondary.url,

      title_name: seoData.title_name ?? "",
      description: seoData.description ?? "",
      image: seoData.image ?? "",
      favicon: seoData.favicon ?? "",
      actual_url: seoData.actual_url ?? "",
      link_url: seoData.link_url ?? "",
      keywords: seoData.keywords ?? "",
      changefreq: seoData.changefreq ?? "",
      priority_screen: seoData.priority_screen ?? "0.8",
    };
  };

  // ─── Save ─────────────────────────────────────────────────────────────────
  const handleSave = async (saveStatus) => {
    const e = validate();
    if (Object.keys(e).length > 0) {
      setErrors(e);
      showError("Please fix the errors");
      return;
    }
    setErrors({});
    setLoading(true);
    try {
      const payload = buildPayload(saveStatus);
      console.log("=== IndustryForm Payload ===", payload);

      const formData = new FormData();
      Object.entries(payload).forEach(([key, value]) => {
        if (typeof value === "boolean") {
          formData.append(key, value ? 1 : 0);
        } else if (typeof value === "object") {
          formData.append(key, JSON.stringify(value));
        } else {
          formData.append(key, value);
        }
      });

      const saveRes = await server_post_data(industry_page_save_update, payload);
      showSuccess(
        mode === "create" ? "Industry created!" : "Industry updated!",
      );
      showSeoWarnings(saveRes);
      navigate("/industries");
    } catch {
      showError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // ─── Shared style helpers ─────────────────────────────────────────────────
  const addBtn =
    "flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-lg";
  const addStyle = {
    background: "hsl(var(--accent-muted))",
    color: "hsl(var(--accent))",
  };
  const cardCls = "p-3 rounded-lg border border-border space-y-3 mb-2";

  // ─── RENDER ───────────────────────────────────────────────────────────────
  return (
    <div className="max-w-[900px] mx-auto space-y-5">
      {/* Header */}
      <div className="page-header">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate("/industries")}
            className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-muted text-muted-foreground transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
          </button>
          <div>
            <h1 className="page-title">
              {mode === "create" ? "Add Industry" : "Edit Industry"}
            </h1>
            <p className="page-subtitle">Industry detail page editor</p>
          </div>
        </div>
      </div>

      {/* Basic Info */}
      <div className="section-panel">
        <div className="section-panel-header">
          <h2 className="section-panel-title">Basic Info</h2>
        </div>
        <div className="section-panel-body space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Industry Title" required error={errors.title}>
              <TextInput
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Food & Beverage"
                error={!!errors.title}
              />
            </Field>
            <Field label="Category">
              <SelectInput
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                options={CATEGORIES}
              />
            </Field>
          </div>
        </div>
      </div>

      {/* ══════════════════════════════════════════════
          1. HERO
      ══════════════════════════════════════════════ */}
      <CollapsibleSection
        title="Hero Section"
        active={sections.hero}
        onToggleActive={() => toggle("hero")}
      >
        <Field label="Main Heading">
          <TextInput
            value={hero.heading}
            onChange={(e) =>
              setHero((h) => ({ ...h, heading: e.target.value }))
            }
            placeholder="ERP Solutions for Food & Beverage"
          />
        </Field>
        <Field label="Highlight Word">
          <TextInput
            value={hero.highlight}
            onChange={(e) =>
              setHero((h) => ({ ...h, highlight: e.target.value }))
            }
            placeholder="Food & Beverage"
          />
        </Field>
        <Field label="Subheading">
          <TextArea
            value={hero.subheading}
            onChange={(e) =>
              setHero((h) => ({ ...h, subheading: e.target.value }))
            }
            placeholder="Supporting description"
          />
        </Field>
        <Field label="Background Image">
          <MediaImageField
            label="Select background image"
            hint="Recommended: 1920×1080px"
            value={hero.bgImageUrl}
            onChange={(v) => setHero((h) => ({ ...h, bgImageUrl: v }))}
            alt={hero.bgImageAlt}
            onAltChange={(v) => setHero((h) => ({ ...h, bgImageAlt: v }))}
          />
        </Field>

        <SectionDivider title="Banner Points" />
        <StringRepeater
          items={bannerPoints}
          setItems={setBannerPoints}
          placeholder="Point"
          addBtn={addBtn}
          addStyle={addStyle}
        />

        <SectionDivider title="Buttons" />
        <CTAGroup
          label="Primary Button"
          text={bannerPrimary.text}
          url={bannerPrimary.url}
          onTextChange={(v) => setBannerPrimary((b) => ({ ...b, text: v }))}
          onUrlChange={(v) => setBannerPrimary((b) => ({ ...b, url: v }))}
        />
        <CTAGroup
          label="Secondary Button"
          text={bannerSecondary.text}
          url={bannerSecondary.url}
          onTextChange={(v) => setBannerSecondary((b) => ({ ...b, text: v }))}
          onUrlChange={(v) => setBannerSecondary((b) => ({ ...b, url: v }))}
        />

        <SectionDivider title="Badges" />
        {bannerBadges.map((badge, i) => (
          <div key={i} className={cardCls}>
            <div className="flex items-center justify-between">
              <p className="text-xs font-semibold text-muted-foreground">
                Badge {i + 1}
              </p>
              <div className="flex items-center gap-2">
                <ReorderButtons
                  index={i}
                  total={bannerBadges.length}
                  onMove={(f, t) =>
                    setBannerBadges(moveItem(bannerBadges, f, t))
                  }
                />
                {bannerBadges.length > 1 && (
                  <button
                    type="button"
                    onClick={() =>
                      setBannerBadges(
                        bannerBadges.filter((_, idx) => idx !== i),
                      )
                    }
                    className="text-destructive"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            </div>
            <Field label="Icon">
              <SelectInput
                value={badge.icon}
                onChange={(e) =>
                  setBannerBadges(
                    bannerBadges.map((x, idx) =>
                      idx === i ? { ...x, icon: e.target.value } : x,
                    ),
                  )
                }
                options={ICON_OPTIONS}
              />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Title">
                <TextInput
                  value={badge.title}
                  onChange={(e) =>
                    setBannerBadges(
                      bannerBadges.map((x, idx) =>
                        idx === i ? { ...x, title: e.target.value } : x,
                      ),
                    )
                  }
                  placeholder="SAP Partner"
                />
              </Field>
              <Field label="Description">
                <TextInput
                  value={badge.subtitle}
                  onChange={(e) =>
                    setBannerBadges(
                      bannerBadges.map((x, idx) =>
                        idx === i ? { ...x, decp: e.target.value } : x,
                      ),
                    )
                  }
                  placeholder="Certified Experts"
                />
              </Field>
            </div>
          </div>
        ))}
        <button
          type="button"
          onClick={() =>
            setBannerBadges([
              ...bannerBadges,
              { icon: "Shield", title: "", decp: "" },
            ])
          }
          className={addBtn}
          style={addStyle}
        >
          <Plus className="w-3.5 h-3.5" /> Add Badge
        </button>
      </CollapsibleSection>

      {/* ══════════════════════════════════════════════
          2. SNAPSHOT
      ══════════════════════════════════════════════ */}
      <CollapsibleSection
        title="Industry Snapshot"
        active={sections.snapshot}
        onToggleActive={() => toggle("snapshot")}
      >
        <Field label="Heading">
          <TextInput
            value={snapshot.heading}
            onChange={(e) =>
              setSnapshot((s) => ({ ...s, heading: e.target.value }))
            }
            placeholder="Driving Growth in Food & Beverage"
          />
        </Field>
        <Field label="Description">
          <TextArea
            rows={4}
            value={snapshot.description}
            onChange={(e) =>
              setSnapshot((s) => ({ ...s, description: e.target.value }))
            }
          />
        </Field>
        <Field label="Section Image">
          <MediaImageField
            label="Upload section image"
            hint="800×500px"
            value={snapshot.imageUrl}
            onChange={(v) => setSnapshot((s) => ({ ...s, imageUrl: v }))}
          />
        </Field>
        <CTAGroup
          label="CTA Button"
          text={snapshot.btnText}
          url={snapshot.btnLink}
          onTextChange={(v) => setSnapshot((s) => ({ ...s, btnText: v }))}
          onUrlChange={(v) => setSnapshot((s) => ({ ...s, btnLink: v }))}
        />
      </CollapsibleSection>

      {/* ══════════════════════════════════════════════
          3. CHALLENGES & SOLUTIONS
      ══════════════════════════════════════════════ */}
      <CollapsibleSection
        title="Challenges & Solutions"
        active={sections.challenges}
        onToggleActive={() => toggle("challenges")}
      >
        <Field label="Heading">
          <TextInput
            value={challenges.heading}
            onChange={(e) =>
              setChallenges((c) => ({ ...c, heading: e.target.value }))
            }
            placeholder="Common Challenges We Solve"
          />
        </Field>
        <Field label="Description">
          <TextArea
            value={challenges.description}
            onChange={(e) =>
              setChallenges((c) => ({ ...c, description: e.target.value }))
            }
          />
        </Field>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-3 border-t border-border">
          <div>
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">
              Challenge Column
            </p>
            <StringRepeater
              items={commonChallenges}
              setItems={setCommonChallenges}
              placeholder="Challenge"
              addBtn={addBtn}
              addStyle={addStyle}
            />
          </div>
          <div>
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">
              Solution Column
            </p>
            <StringRepeater
              items={ourSolutions}
              setItems={setOurSolutions}
              placeholder="Solution"
              addBtn={addBtn}
              addStyle={addStyle}
            />
          </div>
        </div>
        <CTAGroup
          label="CTA Button"
          text={challenges.btnText}
          url={challenges.btnLink}
          onTextChange={(v) => setChallenges((c) => ({ ...c, btnText: v }))}
          onUrlChange={(v) => setChallenges((c) => ({ ...c, btnLink: v }))}
        />
      </CollapsibleSection>

      {/* ══════════════════════════════════════════════
          4. HOW WE HELP
      ══════════════════════════════════════════════ */}
      <CollapsibleSection
        title="How We Help"
        active={sections.weHelp}
        onToggleActive={() => toggle("weHelp")}
      >
        <Field label="Heading">
          <TextInput
            value={weHelp.heading}
            onChange={(e) =>
              setWeHelp((w) => ({ ...w, heading: e.target.value }))
            }
            placeholder="How We Help Your Business"
          />
        </Field>
        <Field label="Description">
          <TextArea
            value={weHelp.description}
            onChange={(e) =>
              setWeHelp((w) => ({ ...w, description: e.target.value }))
            }
          />
        </Field>
        <SectionDivider title="Cards" />
        {weHelpList.map((card, i) => (
          <div key={i} className={cardCls}>
            <div className="flex items-center justify-between">
              <p className="text-xs font-semibold text-muted-foreground">
                Card {i + 1}
              </p>
              <div className="flex items-center gap-2">
                <ReorderButtons
                  index={i}
                  total={weHelpList.length}
                  onMove={(f, t) => setWeHelpList(moveItem(weHelpList, f, t))}
                />
                {weHelpList.length > 1 && (
                  <button
                    type="button"
                    onClick={() =>
                      setWeHelpList(weHelpList.filter((_, idx) => idx !== i))
                    }
                    className="text-destructive"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            </div>
            <Field label="Icon">
              <SelectInput
                value={card.icon}
                onChange={(e) =>
                  setWeHelpList(
                    weHelpList.map((x, idx) =>
                      idx === i ? { ...x, icon: e.target.value } : x,
                    ),
                  )
                }
                options={ICON_OPTIONS}
              />
            </Field>
            <Field label="Title">
              <TextInput
                value={card.title}
                onChange={(e) =>
                  setWeHelpList(
                    weHelpList.map((x, idx) =>
                      idx === i ? { ...x, title: e.target.value } : x,
                    ),
                  )
                }
                placeholder="Card title"
              />
            </Field>
            <Field label="Description">
              <TextArea
                rows={2}
                value={card.desc}
                onChange={(e) =>
                  setWeHelpList(
                    weHelpList.map((x, idx) =>
                      idx === i ? { ...x, description: e.target.value } : x,
                    ),
                  )
                }
              />
            </Field>
          </div>
        ))}
        <button
          type="button"
          onClick={() =>
            setWeHelpList([
              ...weHelpList,
              { icon: "CheckCircle2", title: "", description: "" },
            ])
          }
          className={addBtn}
          style={addStyle}
        >
          <Plus className="w-3.5 h-3.5" /> Add Card
        </button>
        <CTAGroup
          label="CTA Button"
          text={weHelp.btnText}
          url={weHelp.btnLink}
          onTextChange={(v) => setWeHelp((w) => ({ ...w, btnText: v }))}
          onUrlChange={(v) => setWeHelp((w) => ({ ...w, btnLink: v }))}
        />
      </CollapsibleSection>

      {/* ══════════════════════════════════════════════
          5. TECH STACK
      ══════════════════════════════════════════════ */}
      <CollapsibleSection
        title="Technology Stack"
        active={sections.techStack}
        onToggleActive={() => toggle("techStack")}
      >
        <Field label="Heading">
          <TextInput
            value={techStack.heading}
            onChange={(e) =>
              setTechStack((t) => ({ ...t, heading: e.target.value }))
            }
            placeholder="Technology We Use"
          />
        </Field>
        <Field label="Description">
          <TextArea
            value={techStack.description}
            onChange={(e) =>
              setTechStack((t) => ({ ...t, description: e.target.value }))
            }
          />
        </Field>
        <SectionDivider title="Tech Items" />
        {techStackList.map((tech, i) => (
          <div key={i} className={cardCls}>
            <div className="flex items-center justify-between">
              <p className="text-xs font-semibold text-muted-foreground">
                Item {i + 1}
              </p>
              <div className="flex items-center gap-2">
                <ReorderButtons
                  index={i}
                  total={techStackList.length}
                  onMove={(f, t) =>
                    setTechStackList(moveItem(techStackList, f, t))
                  }
                />
                {techStackList.length > 1 && (
                  <button
                    type="button"
                    onClick={() =>
                      setTechStackList(
                        techStackList.filter((_, idx) => idx !== i),
                      )
                    }
                    className="text-destructive"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            </div>
            <Field label="Title">
              <TextInput
                value={tech.title}
                onChange={(e) =>
                  setTechStackList(
                    techStackList.map((x, idx) =>
                      idx === i ? { ...x, title: e.target.value } : x,
                    ),
                  )
                }
                placeholder="SAP Business One"
              />
            </Field>
            <Field label="Description">
              <TextInput
                value={tech.desc}
                onChange={(e) =>
                  setTechStackList(
                    techStackList.map((x, idx) =>
                      idx === i ? { ...x, desc: e.target.value } : x,
                    ),
                  )
                }
                placeholder="Core ERP for finance and production."
              />
            </Field>
            <Field label="Link">
              <TextInput
                value={tech.link}
                onChange={(e) =>
                  setTechStackList(
                    techStackList.map((x, idx) =>
                      idx === i ? { ...x, link: e.target.value } : x,
                    ),
                  )
                }
                placeholder="/products/sap-b1"
              />
            </Field>
          </div>
        ))}
        <button
          type="button"
          onClick={() =>
            setTechStackList([
              ...techStackList,
              { title: "", desc: "", link: "" },
            ])
          }
          className={addBtn}
          style={addStyle}
        >
          <Plus className="w-3.5 h-3.5" /> Add Item
        </button>
        <CTAGroup
          label="CTA Button"
          text={techStack.btnText}
          url={techStack.btnLink}
          onTextChange={(v) => setTechStack((t) => ({ ...t, btnText: v }))}
          onUrlChange={(v) => setTechStack((t) => ({ ...t, btnLink: v }))}
        />
      </CollapsibleSection>

      {/* ══════════════════════════════════════════════
          6. FINAL CTA
      ══════════════════════════════════════════════ */}
      <CollapsibleSection
        title="Final CTA Section"
        active={sections.finalCta}
        onToggleActive={() => toggle("finalCta")}
      >
        <Field label="Heading">
          <TextInput
            value={finalCta.heading}
            onChange={(e) =>
              setFinalCta((c) => ({ ...c, heading: e.target.value }))
            }
            placeholder="Ready to Transform Your Business?"
          />
        </Field>
        <Field label="Description">
          <TextArea
            value={finalCta.description}
            onChange={(e) =>
              setFinalCta((c) => ({ ...c, description: e.target.value }))
            }
          />
        </Field>
        <CTAGroup
          label="Primary Button"
          text={ctaPrimary.text}
          url={ctaPrimary.url}
          onTextChange={(v) => setCtaPrimary((b) => ({ ...b, text: v }))}
          onUrlChange={(v) => setCtaPrimary((b) => ({ ...b, url: v }))}
        />
        <CTAGroup
          label="Secondary Button"
          text={ctaSecondary.text}
          url={ctaSecondary.url}
          onTextChange={(v) => setCtaSecondary((b) => ({ ...b, text: v }))}
          onUrlChange={(v) => setCtaSecondary((b) => ({ ...b, url: v }))}
        />
      </CollapsibleSection>

      {/* SEO */}
      <SEOSection seoData={seoData} onChange={handleSeoChange} />

      <FormActions
        onCancel={() => navigate("/industries")}
        onSaveDraft={() => handleSave("draft")}
        onPublish={() => handleSave("published")}
        loading={loading}
      />
    </div>
  );
}
